import React, { useState, useEffect } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Tooltip,
  Tabs,
  Tab
} from "@mui/material";
import {
  Close as CloseIcon,
  Notifications as NotificationsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import {
  getBudgetGroupAlerts,
  createBudgetGroupAlert,
  updateBudgetGroupAlert,
  deleteBudgetGroupAlert,
  getBudgetGroupScheduledReports,
  createBudgetGroupScheduledReport,
  updateBudgetGroupScheduledReport,
  deleteBudgetGroupScheduledReport,
  clearBudgetMessages,
} from "../../../store/spendCategorization/budgetSlice";
import { useUsers } from "../../../components/Common/HelperComponents/GetDataFromStore";

const BudgetGroupAlertsModal = ({ open, onClose, rgBudgetId, budgetGroupName }) => {
  const dispatch = useDispatch();
  const { users } = useUsers();

  // Redux state
  const {
    isFetchingBudgetGroupAlerts,
    budgetGroupAlerts,
    fetchBudgetGroupAlertsError,
    isCreatingBudgetGroupAlert,
    createBudgetGroupAlertSuccess,
    createBudgetGroupAlertError,
    isUpdatingBudgetGroupAlert,
    updateBudgetGroupAlertSuccess,
    updateBudgetGroupAlertError,
    isDeletingBudgetGroupAlert,
    deleteBudgetGroupAlertSuccess,
    deleteBudgetGroupAlertError,
    // Scheduled Reports state
    isFetchingBudgetGroupScheduledReports,
    budgetGroupScheduledReports,
    fetchBudgetGroupScheduledReportsError,
    isCreatingBudgetGroupScheduledReport,
    createBudgetGroupScheduledReportSuccess,
    createBudgetGroupScheduledReportError,
    isUpdatingBudgetGroupScheduledReport,
    updateBudgetGroupScheduledReportSuccess,
    updateBudgetGroupScheduledReportError,
    isDeletingBudgetGroupScheduledReport,
    deleteBudgetGroupScheduledReportSuccess,
    deleteBudgetGroupScheduledReportError,
  } = useSelector((state) => state.budget);

  // Local state
  const [activeTab, setActiveTab] = useState(0);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [formData, setFormData] = useState({
    threshold_percent: "",
    interval_days: "",
    notify_emails: [],
  });
  const [errors, setErrors] = useState({});
  const [showUpdatedColumns, setShowUpdatedColumns] = useState(true);

  // Fetch data when modal opens
  useEffect(() => {
    if (open && rgBudgetId) {
      dispatch(getBudgetGroupAlerts(rgBudgetId));
      dispatch(getBudgetGroupScheduledReports(rgBudgetId));
    }
  }, [open, rgBudgetId, dispatch]);

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Handle success messages
  useEffect(() => {
    if (createBudgetGroupAlertSuccess) {
      setCreateModalOpen(false);
      setFormData({ threshold_percent: "", notify_emails: [] });
      setErrors({});
      dispatch(getBudgetGroupAlerts(rgBudgetId));
      dispatch(clearBudgetMessages());
    }
  }, [createBudgetGroupAlertSuccess, dispatch, rgBudgetId]);

  useEffect(() => {
    if (updateBudgetGroupAlertSuccess) {
      setEditModalOpen(false);
      setFormData({ threshold_percent: "", notify_emails: [] });
      setErrors({});
      dispatch(getBudgetGroupAlerts(rgBudgetId));
      dispatch(clearBudgetMessages());
    }
  }, [updateBudgetGroupAlertSuccess, dispatch, rgBudgetId]);

  useEffect(() => {
    if (deleteBudgetGroupAlertSuccess) {
      setDeleteDialogOpen(false);
      setSelectedAlert(null);
      dispatch(getBudgetGroupAlerts(rgBudgetId));
      dispatch(clearBudgetMessages());
    }
  }, [deleteBudgetGroupAlertSuccess, dispatch, rgBudgetId]);

  // Handle scheduled reports success messages
  useEffect(() => {
    if (createBudgetGroupScheduledReportSuccess) {
      setCreateModalOpen(false);
      setFormData({ threshold_percent: "", interval_days: "", notify_emails: [] });
      setErrors({});
      dispatch(getBudgetGroupScheduledReports(rgBudgetId));
      dispatch(clearBudgetMessages());
    }
  }, [createBudgetGroupScheduledReportSuccess, dispatch, rgBudgetId]);

  useEffect(() => {
    if (updateBudgetGroupScheduledReportSuccess) {
      setEditModalOpen(false);
      setFormData({ threshold_percent: "", interval_days: "", notify_emails: [] });
      setErrors({});
      dispatch(getBudgetGroupScheduledReports(rgBudgetId));
      dispatch(clearBudgetMessages());
    }
  }, [updateBudgetGroupScheduledReportSuccess, dispatch, rgBudgetId]);

  useEffect(() => {
    if (deleteBudgetGroupScheduledReportSuccess) {
      setDeleteDialogOpen(false);
      setSelectedAlert(null);
      dispatch(getBudgetGroupScheduledReports(rgBudgetId));
      dispatch(clearBudgetMessages());
    }
  }, [deleteBudgetGroupScheduledReportSuccess, dispatch, rgBudgetId]);

  const handleCreateAlert = () => {
    setCreateModalOpen(true);
    setFormData({ threshold_percent: "", interval_days: "", notify_emails: [] });
    setErrors({});
  };

  const handleEditScheduledReport = (report) => {
    setSelectedAlert(report);
    setFormData({
      interval_days: report.interval_days.toString(),
      notify_emails: report.notify_emails,
    });
    setErrors({});
    setEditModalOpen(true);
  };

  const handleDeleteScheduledReport = (report) => {
    setSelectedAlert(report);
    setDeleteDialogOpen(true);
  };

  const handleEditAlert = (alert) => {
    setSelectedAlert(alert);
    setFormData({
      threshold_percent: alert.threshold_percent.toString(),
      notify_emails: alert.notify_emails,
    });
    setErrors({});
    setEditModalOpen(true);
  };

  const handleDeleteAlert = (alert) => {
    setSelectedAlert(alert);
    setDeleteDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate based on active tab
    if (activeTab === 0) {
      // Threshold Alerts validation
      if (!formData.threshold_percent) {
        newErrors.threshold_percent = "Threshold percentage is required";
      } else {
        const threshold = parseFloat(formData.threshold_percent);
        if (isNaN(threshold) || threshold <= 0 || threshold > 100) {
          newErrors.threshold_percent = "Threshold must be between 1 and 100";
        }
      }
    } else {
      // Scheduled Reports validation
      if (!formData.interval_days) {
        newErrors.interval_days = "Interval days is required";
      } else {
        const interval = parseInt(formData.interval_days);
        if (isNaN(interval) || interval <= 0 || interval > 365) {
          newErrors.interval_days = "Interval must be between 1 and 365 days";
        }
      }
    }

    // Validate recipients
    if (!formData.notify_emails || formData.notify_emails.length === 0) {
      newErrors.notify_emails = "At least one recipient is required";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (activeTab === 0) {
      // Threshold Alerts
      const payload = {
        rg_budget_id: rgBudgetId,
        threshold_percent: parseFloat(formData.threshold_percent),
        notify_emails: formData.notify_emails,
      };

      if (editModalOpen) {
        dispatch(updateBudgetGroupAlert({ alertId: selectedAlert.id, updateData: payload }));
      } else {
        dispatch(createBudgetGroupAlert(payload));
      }
    } else {
      // Scheduled Reports
      const payload = {
        rg_budget_id: rgBudgetId,
        interval_days: parseInt(formData.interval_days),
        notify_emails: formData.notify_emails,
      };

      if (editModalOpen) {
        dispatch(updateBudgetGroupScheduledReport({ reportId: selectedAlert.id, updateData: payload }));
      } else {
        dispatch(createBudgetGroupScheduledReport(payload));
      }
    }
  };

  const handleDeleteConfirm = () => {
    if (selectedAlert) {
      if (activeTab === 0) {
        dispatch(deleteBudgetGroupAlert(selectedAlert.id));
      } else {
        dispatch(deleteBudgetGroupScheduledReport(selectedAlert.id));
      }
    }
  };

  const handleCloseModal = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setFormData({ threshold_percent: "", interval_days: "", notify_emails: [] });
    setErrors({});
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <>
      {/* Main Modal */}
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="budget-group-alerts-modal-title"
        aria-describedby="budget-group-alerts-modal-description"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "95%", sm: "90%", md: "80%" },
            maxWidth: "1200px",
            maxHeight: "90vh",
            bgcolor: "background.paper",
            borderRadius: "12px",
            boxShadow: 24,
            p: 0,
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 3,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 4px rgba(245, 158, 11, 0.3)",
                }}
              >
                <NotificationsIcon sx={{ fontSize: 20, color: "white" }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "1.125rem",
                  }}
                >
                  Budget Group Alerts
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {budgetGroupName || "Manage budget group notifications"}
                </Typography>
              </Box>
            </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateAlert}
              disabled={activeTab === 0 ? budgetGroupAlerts.length >= 3 : budgetGroupScheduledReports.length >= 3}
              sx={{
                backgroundColor: "#3b82f6",
                "&:hover": {
                  backgroundColor: "#2563eb",
                },
                "&:disabled": {
                  backgroundColor: "#e5e7eb",
                  color: "#9ca3af",
                },
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              {activeTab === 0 ? "Create Alert" : "Create Report"}
            </Button>
              <IconButton
                onClick={onClose}
                sx={{
                  color: "#64748b",
                  "&:hover": {
                    backgroundColor: "#f1f5f9",
                    color: "#374151",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Information Banner */}
          <Box sx={{ 
            px: 3, 
            py: 2, 
            backgroundColor: "#f8fafc", 
            borderBottom: "1px solid #e2e8f0",
            display: "flex",
            alignItems: "center",
            gap: 1
          }}>
            <Box sx={{
              width: 20,
              height: 20,
              borderRadius: "50%",
              backgroundColor: "#3b82f6",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0
            }}>
              <Typography variant="caption" sx={{ color: "white", fontWeight: 600, fontSize: "0.75rem" }}>
                i
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ color: "#64748b", fontSize: "0.875rem" }}>
              You can configure up to 3 threshold alerts and 3 scheduled reports per budget group.
            </Typography>
          </Box>

          {/* Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="budget group alerts tabs">
              <Tab label="Threshold Alerts" />
              <Tab label="Scheduled Reports" />
            </Tabs>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3, maxHeight: "calc(90vh - 120px)", overflow: "auto" }}>
            {/* Tab Panel 0: Threshold Alerts */}
            {activeTab === 0 && (
              <>
                {/* Error Messages */}
                {fetchBudgetGroupAlertsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {fetchBudgetGroupAlertsError}
                  </Alert>
                )}

                {/* Loading State */}
                {isFetchingBudgetGroupAlerts ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  /* Alerts Table */
                  <TableContainer component={Paper} sx={{ borderRadius: "8px", overflow: "hidden", overflowX: "auto" }}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Threshold</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Recipients</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Created</TableCell>
                      {showUpdatedColumns && (
                        <>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Updated</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Updated By</TableCell>
                        </>
                      )}
                      <TableCell sx={{ fontWeight: 600, color: "#374151", textAlign: "center" }}>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {budgetGroupAlerts.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ textAlign: "center", py: 4, color: "#64748b" }}>
                          No alerts configured for this budget group
                        </TableCell>
                      </TableRow>
                    ) : (
                      budgetGroupAlerts.map((alert) => (
                        <TableRow key={alert.id} hover>
                          <TableCell>
                            <Chip
                              label={`${alert.threshold_percent}%`}
                              sx={{
                                backgroundColor: "#fef3c7",
                                color: "#92400e",
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {alert.notify_emails.slice(0, 2).map((email, index) => (
                                <Chip
                                  key={index}
                                  label={email}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#e0f2fe",
                                    color: "#0369a1",
                                    fontSize: "0.75rem",
                                  }}
                                />
                              ))}
                              {alert.notify_emails.length > 2 && (
                                <Chip
                                  label={`+${alert.notify_emails.length - 2} more`}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#f3f4f6",
                                    color: "#6b7280",
                                    fontSize: "0.75rem",
                                  }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                            {formatDate(alert.created_at)}
                          </TableCell>
                          {showUpdatedColumns && (
                            <>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {formatDate(alert.updated_at)}
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {alert.updated_by || "—"}
                              </TableCell>
                            </>
                          )}
                          <TableCell sx={{ textAlign: "center" }}>
                            <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                              <IconButton
                                size="small"
                                onClick={() => handleEditAlert(alert)}
                                sx={{ color: "#3b82f6" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteAlert(alert)}
                                sx={{ color: "#ef4444" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* Tab Panel 1: Scheduled Reports */}
            {activeTab === 1 && (
              <>
                {/* Error Messages */}
                {fetchBudgetGroupScheduledReportsError && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {fetchBudgetGroupScheduledReportsError}
                  </Alert>
                )}

                {/* Loading State */}
                {isFetchingBudgetGroupScheduledReports ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  /* Scheduled Reports Table */
                  <TableContainer component={Paper} sx={{ borderRadius: "8px", overflow: "hidden", overflowX: "auto" }}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Interval</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Next Run</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Recipients</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Created</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Created By</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Updated</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151" }}>Updated By</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: "#374151", textAlign: "center" }}>
                            Actions
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {budgetGroupScheduledReports.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, color: "#64748b" }}>
                              No scheduled reports configured for this budget group
                            </TableCell>
                          </TableRow>
                        ) : (
                          budgetGroupScheduledReports.map((report) => (
                            <TableRow key={report.id} hover>
                              <TableCell>
                                <Chip
                                  label={`${report.interval_days} days`}
                                  sx={{
                                    backgroundColor: "#e0f2fe",
                                    color: "#0369a1",
                                    fontWeight: 600,
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {formatDate(report.next_run_at)}
                              </TableCell>
                              <TableCell>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                  {report.notify_emails.slice(0, 2).map((email, index) => (
                                    <Chip
                                      key={index}
                                      label={email}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#e0f2fe",
                                        color: "#0369a1",
                                        fontSize: "0.75rem",
                                      }}
                                    />
                                  ))}
                                  {report.notify_emails.length > 2 && (
                                    <Chip
                                      label={`+${report.notify_emails.length - 2} more`}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#f3f4f6",
                                        color: "#6b7280",
                                        fontSize: "0.75rem",
                                      }}
                                    />
                                  )}
                                </Box>
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {formatDate(report.created_at)}
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {report.created_by || "—"}
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {formatDate(report.updated_at)}
                              </TableCell>
                              <TableCell sx={{ color: "#64748b", fontSize: "0.875rem" }}>
                                {report.updated_by || "—"}
                              </TableCell>
                              <TableCell sx={{ textAlign: "center" }}>
                                <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditScheduledReport(report)}
                                    sx={{ color: "#3b82f6" }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteScheduledReport(report)}
                                    sx={{ color: "#ef4444" }}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        </Box>
      </Modal>

      {/* Create/Edit Alert Modal */}
      <Dialog
        open={createModalOpen || editModalOpen}
        onClose={() => {
          setCreateModalOpen(false);
          setEditModalOpen(false);
          setFormData({ threshold_percent: "", notify_emails: [] });
          setErrors({});
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
          },
        }}
      >
        <form onSubmit={handleSubmit}>
          <DialogTitle sx={{ p: 0 }}>
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid #e2e8f0",
                backgroundColor: "#f8fafc",
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: "0 2px 4px rgba(245, 158, 11, 0.3)",
                }}
              >
                <NotificationsIcon sx={{ fontSize: 20, color: "white" }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#1e293b",
                    fontSize: "1.125rem",
                  }}
                >
                  {editModalOpen 
                    ? (activeTab === 0 ? "Edit Alert" : "Edit Scheduled Report")
                    : (activeTab === 0 ? "Create Alert" : "Create Scheduled Report")
                  }
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  {editModalOpen 
                    ? (activeTab === 0 ? "Update alert settings and recipients" : "Update scheduled report settings")
                    : (activeTab === 0 ? "Set up budget threshold notifications" : "Set up scheduled budget reports")
                  }
                </Typography>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pt: 1 }}>
              {/* Error Messages */}
              {createBudgetGroupAlertError && (
                <Alert severity="error">{createBudgetGroupAlertError}</Alert>
              )}
              {updateBudgetGroupAlertError && (
                <Alert severity="error">{updateBudgetGroupAlertError}</Alert>
              )}
              {createBudgetGroupScheduledReportError && (
                <Alert severity="error">{createBudgetGroupScheduledReportError}</Alert>
              )}
              {updateBudgetGroupScheduledReportError && (
                <Alert severity="error">{updateBudgetGroupScheduledReportError}</Alert>
              )}

              {/* Dynamic field based on active tab */}
              {activeTab === 0 ? (
                /* Threshold Percentage */
                <TextField
                  label="Threshold Percentage"
                  type="number"
                  value={formData.threshold_percent}
                  onChange={(e) =>
                    setFormData({ ...formData, threshold_percent: e.target.value })
                  }
                  error={!!errors.threshold_percent}
                  helperText={errors.threshold_percent || "Enter a value between 1 and 100"}
                  fullWidth
                  inputProps={{ min: 1, max: 100, step: 0.1 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              ) : (
                /* Interval Days */
                <TextField
                  label="Interval Days"
                  type="number"
                  value={formData.interval_days}
                  onChange={(e) =>
                    setFormData({ ...formData, interval_days: e.target.value })
                  }
                  error={!!errors.interval_days}
                  helperText={errors.interval_days || "The period can be between 1 to 365 days"}
                  fullWidth
                  inputProps={{ min: 1, max: 365, step: 1 }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              )}

              {/* Recipients */}
              <FormControl fullWidth error={!!errors.notify_emails}>
                <InputLabel>Recipients</InputLabel>
                <Select
                  multiple
                  value={formData.notify_emails}
                  onChange={(e) =>
                    setFormData({ ...formData, notify_emails: e.target.value })
                  }
                  input={<OutlinedInput label="Recipients" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: "60px", overflow: "auto" }}>
                      {selected.map((value) => (
                        <Chip
                          key={value}
                          label={value.length > 20 ? `${value.substring(0, 20)}...` : value}
                          size="small"
                          sx={{
                            backgroundColor: "#e0f2fe",
                            color: "#0369a1",
                            fontSize: "0.75rem",
                          }}
                        />
                      ))}
                    </Box>
                  )}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                        minWidth: 250,
                      },
                    },
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                >
                  {users.map((user) => (
                    <MenuItem key={user.email} value={user.email}>
                      <Checkbox checked={formData.notify_emails.indexOf(user.email) > -1} />
                      <ListItemText
                        primary={user.email}
                        secondary={user.name}
                        primaryTypographyProps={{
                          style: {
                            fontSize: "0.875rem",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            maxWidth: "200px",
                          },
                        }}
                        secondaryTypographyProps={{
                          style: {
                            fontSize: "0.75rem",
                            color: "#64748b",
                          },
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
                {errors.notify_emails && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.notify_emails}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 3, gap: 1 }}>
            <Button
              onClick={handleCloseModal}
              sx={{
                color: "#64748b",
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isCreatingBudgetGroupAlert || isUpdatingBudgetGroupAlert || isCreatingBudgetGroupScheduledReport || isUpdatingBudgetGroupScheduledReport}
              sx={{
                backgroundColor: "#3b82f6",
                "&:hover": {
                  backgroundColor: "#2563eb",
                },
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                minWidth: "100px",
              }}
            >
              {isCreatingBudgetGroupAlert || isUpdatingBudgetGroupAlert || isCreatingBudgetGroupScheduledReport || isUpdatingBudgetGroupScheduledReport ? (
                <CircularProgress size={20} color="inherit" />
              ) : editModalOpen ? (
                activeTab === 0 ? "Update" : "Update Report"
              ) : (
                activeTab === 0 ? "Create" : "Create Report"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedAlert(null);
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              p: 3,
              borderBottom: "1px solid #e2e8f0",
              backgroundColor: "#f8fafc",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 4px rgba(239, 68, 68, 0.3)",
              }}
            >
              <DeleteIcon sx={{ fontSize: 20, color: "white" }} />
            </Box>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "#1e293b",
                  fontSize: "1.125rem",
                }}
              >
                {activeTab === 0 ? "Delete Alert" : "Delete Scheduled Report"}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "#64748b",
                  fontSize: "0.875rem",
                  fontWeight: 500,
                }}
              >
                This action cannot be undone
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this alert?
          </Typography>
          {selectedAlert && (
            <Box sx={{ p: 2, backgroundColor: "#f8fafc", borderRadius: "8px" }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {activeTab === 0 ? "Alert Details:" : "Report Details:"}
              </Typography>
              {activeTab === 0 ? (
                <>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Threshold: {selectedAlert.threshold_percent}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Recipients: {selectedAlert.notify_emails.join(", ")}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Interval: {selectedAlert.interval_days} days
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#64748b" }}>
                    Recipients: {selectedAlert.notify_emails.join(", ")}
                  </Typography>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedAlert(null);
            }}
            sx={{
              color: "#64748b",
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            disabled={isDeletingBudgetGroupAlert || isDeletingBudgetGroupScheduledReport}
            sx={{
              backgroundColor: "#ef4444",
              "&:hover": {
                backgroundColor: "#dc2626",
              },
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              minWidth: "100px",
            }}
          >
            {isDeletingBudgetGroupAlert || isDeletingBudgetGroupScheduledReport ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BudgetGroupAlertsModal;
